import type { ActivityRetentionYears } from './activityRetention'
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError, getJSON, postJSON } from '@/lib/api'
import type { ConnectionType } from './detailTypes'
import { connectionNavigation } from './connectionNavigation'

export type PendingAuthorization = {
  id: number
  connection_type: string
  connection_id: number | null
}
export type DuplicateConnection = {
  code: string
  id: number
  display_name: string
  restorable: boolean
}
type ReturnResponse = { connection_oauth_authorization: PendingAuthorization }
const oauthPath = '/api/v1/connection_oauth_authorizations'

export function connectionError(error: Error | null) {
  if (!(error instanceof ApiError)) return error?.message
  const body = error.body as { errors?: Record<string, string[]> }
  return body.errors ? Object.values(body.errors).flat().join('; ') : error.message
}
export function duplicateConnection(error: Error | null) {
  if (!(error instanceof ApiError) || error.status !== 409) return undefined
  const body = error.body as { error?: DuplicateConnection }
  return body.error?.code === 'already_connected' ? body.error : undefined
}
export function useAddConnection(onClose: () => void, pending?: PendingAuthorization) {
  const [selected, setSelected] = useState<ConnectionType>()
  const [name, setName] = useState('')
  const [date, setDate] = useState('')
  const [retention, setRetention] = useState<ActivityRetentionYears>(null)
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const types = useQuery({
    queryKey: ['connection-types'],
    queryFn: () =>
      getJSON<{
        connection_types: ConnectionType[]
        legal_identity_country?: string | null
        activity_retention_supported?: boolean
      }>('/api/v1/connection_types'),
  })
  const retentionSupported = types.data?.activity_retention_supported === true
  const retentionAttributes = retentionSupported ? { activity_retention_years: retention } : {}
  const type = pending
    ? types.data?.connection_types.find((item) => item.type === pending.connection_type)
    : selected
  const finish = (result: { connection: { id: number } }) => {
    onClose()
    connectionNavigation.assign(`/settings/connections/${result.connection.id}`)
  }
  const mutation = useMutation({
    mutationFn: async () => {
      if (pending)
        return postJSON<{ connection: { id: number } }>(`${oauthPath}/${pending.id}/complete`, {
          display_name: name,
          import_from: date || null,
          ...retentionAttributes,
        })
      return postJSON<{ connection: { id: number } }>('/api/v1/connections', {
        connection: {
          connection_type: type?.type,
          display_name: name,
          credentials,
          import_from: date || null,
          ...retentionAttributes,
        },
      })
    },
    onSuccess: finish,
  })
  const start = useMutation({
    mutationFn: () =>
      postJSON<{ connection_oauth_authorization: { authorization_url: string } }>(oauthPath, {
        connection_type: type?.type,
      }),
    onSuccess: (result) =>
      connectionNavigation.assign(result.connection_oauth_authorization.authorization_url),
  })
  const restore = useMutation({
    mutationFn: (id: number) =>
      postJSON<{ connection: { id: number } }>(
        `/api/v1/connections/${id}/restore`,
        pending ? { connection_oauth_authorization_id: pending.id } : { credentials },
      ),
    onSuccess: finish,
  })
  const choose = (next?: ConnectionType) => {
    setSelected(next)
    setCredentials({})
    setName('')
    setDate('')
    setRetention(null)
    mutation.reset()
    start.reset()
    restore.reset()
  }
  return {
    type,
    types,
    choose,
    name,
    setName,
    date,
    setDate,
    retention,
    retentionSupported,
    setRetention,
    credentials,
    setCredentials,
    mutation,
    start,
    restore,
  }
}

export function useConnectionReturn(enabled: boolean) {
  const started = useRef(false)
  const [pending, setPending] = useState<PendingAuthorization>()
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (started.current) return
    const params = new URLSearchParams(window.location.hash.slice(1))
    if (!params.has('state') && !params.has('error')) return
    started.current = true
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search,
    )
    if (!enabled) return
    if (params.has('error')) {
      setError('Vendor authorisation was not completed. Try connecting again.')
      return
    }
    setLoading(true)
    void postJSON<ReturnResponse>(`${oauthPath}/callback`, {
      state: params.get('state'),
      code: params.get('code'),
    })
      .then(async ({ connection_oauth_authorization: authorization }) => {
        if (authorization.connection_id) {
          await postJSON(`${oauthPath}/${authorization.id}/complete`, {})
          connectionNavigation.assign(`/settings/connections/${authorization.connection_id}`)
        } else setPending(authorization)
      })
      .catch((failure: Error) => setError(connectionError(failure)))
      .finally(() => setLoading(false))
  }, [enabled])
  return {
    pending,
    error,
    loading,
    clear: () => {
      setPending(undefined)
      setError(undefined)
    },
  }
}
